import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import api from 'utils/api';

const DIALOG_Z_INDEX = 1500;

function normalizeTerm(term) {
  return String(term || '').trim().replace(/[.;\s]+$/, '');
}

function termKey(term) {
  return normalizeTerm(term).toLowerCase();
}

function existingTermKeys(terms) {
  return new Set(
    String(terms || '')
      .split(/[;\r\n]+/)
      .map(termKey)
      .filter(Boolean)
  );
}

function suggestionRequest(offer = {}) {
  return {
    fulfillment: offer.fulfillment || offer.description?.fulfillment || '',
  };
}

function newTerms(existingTerms, termsToAdd) {
  const seen = existingTermKeys(existingTerms);
  return termsToAdd
    .flatMap((term) => String(term || '').split(/[;\r\n]+/))
    .map((term) => term.trim())
    .filter((term) => {
      const normalized = termKey(term);
      if (!normalized || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
}

function appendTerms(existingTerms, termsToAdd) {
  const existing = String(existingTerms || '').trim();
  const additions = newTerms(existing, termsToAdd);
  return [existing, ...additions].filter(Boolean).reduce(
    (combined, term) => {
      if (!combined) return term;
      const separator = /[.!?;]$/.test(combined) ? ' ' : '; ';
      return `${combined}${separator}${term}`;
    },
    ''
  );
}

function errorMessage(error) {
  return error?.message
    || error?.error
    || 'Terms and conditions could not be loaded. You can still enter your own terms.';
}

export default function TermsAndConditionsSuggestionsDialog({
  open,
  onClose,
  offer,
  existingTerms = '',
  onApply,
  allowCustomTerm = false,
  disabled = false,
}) {
  const { fulfillment } = suggestionRequest(offer);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [customText, setCustomText] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!open) return undefined;

    setSuggestions([]);
    setSelectedCodes([]);
    setCustomText('');
    setLoadError('');
    setLoading(false);

    if (!fulfillment) {
      setLoadError('Select whether this offer is for retail, services, or both first.');
      return undefined;
    }

    let active = true;
    setLoading(true);
    api.post('/terms-and-conditions/suggestions', {
      fulfillment,
    })
      .then((response) => {
        if (!active) return;
        setSuggestions(Array.isArray(response?.suggestions) ? response.suggestions : []);
      })
      .catch((error) => {
        if (active) setLoadError(errorMessage(error));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    fulfillment,
    open,
  ]);

  const groupedSuggestions = useMemo(
    () => suggestions.reduce((groups, suggestion) => {
      const group = suggestion.group || 'OTHER';
      groups[group] = [...(groups[group] || []), suggestion];
      return groups;
    }, {}),
    [suggestions]
  );

  const addedTerms = existingTermKeys(existingTerms);
  const toggleSuggestion = (code) => {
    setSelectedCodes((previous) => (
      previous.includes(code)
        ? previous.filter((selectedCode) => selectedCode !== code)
        : [...previous, code]
    ));
  };

  const selectedTerms = newTerms(existingTerms, [
    ...suggestions.filter((suggestion) => selectedCodes.includes(suggestion.code))
      .map((suggestion) => suggestion.text),
    customText,
  ]);
  const applySelected = () => {
    if (disabled || !selectedTerms.length) return;
    onApply(selectedTerms);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{ zIndex: DIALOG_Z_INDEX }}
    >
      <DialogTitle>Browse terms and conditions</DialogTitle>

      <DialogContent
        dividers
        sx={{
          minHeight: 220,
          overflowY: 'scroll',
          '::-webkit-scrollbar': {
            width: 10,
          },
          '::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '5px',
            '&:hover': {
              background: '#555',
            },
          },
          '::-webkit-scrollbar-track': {
            background: '#f0f0f0',
          },
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select only the terms that apply to this offer.
        </Typography>

        {loading && (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 5 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              Loading terms and conditions…
            </Typography>
          </Stack>
        )}

        {!loading && loadError && <Alert severity="warning">{loadError}</Alert>}

        {!loading && !loadError && suggestions.length === 0 && (
          <Alert severity="info">
            No terms are available for this offer. Add a custom term instead.
          </Alert>
        )}

        {!loading && !loadError && Object.entries(groupedSuggestions).map(([group, terms]) => (
          <Box
            key={group}
            sx={{ mb: 2 }}
          >
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{
                mb: 0.25,
                textTransform: 'capitalize',
              }}
            >
              {group.replaceAll('_', ' ').toLocaleLowerCase()}
            </Typography>
            <Stack>
              {terms.map((suggestion) => {
                const alreadyAdded = addedTerms.has(termKey(suggestion.text));
                return (
                  <FormControlLabel
                    key={suggestion.code}
                    disabled={disabled || alreadyAdded}
                    control={(
                      <Checkbox
                        checked={selectedCodes.includes(suggestion.code)}
                        onChange={() => toggleSuggestion(suggestion.code)}
                        size="small"
                        sx={{ p: 0, mr: 1 }}
                      />
                    )}
                    label={(
                      <Typography variant="body2">
                        {suggestion.text}
                        {alreadyAdded && (
                          <Typography component="span" variant="caption" color="text.secondary">
                            {' '}— already added
                          </Typography>
                        )}
                      </Typography>
                    )}
                    sx={{
                      alignItems: 'center',
                      ml: 0,
                      mr: 0,
                      py: 0.5,
                    }}
                  />
                );
              })}
            </Stack>
          </Box>
        ))}

        {allowCustomTerm && (
          <TextField
            disabled={disabled}
            label="Custom term or T&C link"
            value={customText}
            onChange={(event) => setCustomText(event.target.value)}
            multiline
            minRows={2}
            fullWidth
            sx={{ mt: 1 }}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={applySelected}
          disabled={disabled || !selectedTerms.length}
        >
          Add selected terms
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export { appendTerms, normalizeTerm, suggestionRequest };
